

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const TrainingModuleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [module, setModule] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/training-modules/${id}`);
            setModule(response.data);
        } catch (error) {
            console.error('Error fetching training module details:', error);
            setSnackbar({ open: true, message: 'Error loading training module details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setModule(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/training-modules/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Training Module updated successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            console.error('Error updating Training Module:', error);
            setSnackbar({ open: true, message: 'Error updating Training Module', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this Training Module?')) {
            try {
                await axiosInstance.delete(`/training-modules/${id}`);
                setSnackbar({ open: true, message: 'Training Module deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/training-modules'), 1000);
            } catch (error) {
                console.error('Error deleting Training Module:', error);
                setSnackbar({ open: true, message: 'Error deleting Training Module', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} sm={item.fullWidth ? 12 : 6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? module.created_by : module.updated_by, module[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {module[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={module?.[item.field]}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                                multiline={item.multiline}
                                type={item.type}
                                options={item.options}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><Skeleton variant="rectangular" height={400} /></MDBox>
                
            </DashboardLayout>
        );
    }

    if (!module) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Training Module not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    const trainingForOptions = [
        { value: 'Guide', label: 'Guide' },
        { value: 'Supplier', label: 'Supplier' },
        { value: 'Both', label: 'Both' }
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{module.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Training Module Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Training Module">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Training Module">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Training Title", field: "name" },
                                    { label: "Content URL", field: "content_url", fullWidth: true },
                                    { label: "Intended For", field: "training_for", options: trainingForOptions },
                                    { label: "Active", field: "active", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }] },
                                    { label: "Description", field: "description", fullWidth: true, multiline: true },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TrainingModuleDetail;
